import { supabase } from '../lib/supabase'
import type {
  SupportCenterParameter,
  SupportCenterSchedulingLimits,
} from '../types/supportCenterParameters'
import {
  DEFAULT_SCHEDULING_LIMITS,
  SUPPORT_CENTER_PARAM_DIAS_MAX,
  SUPPORT_CENTER_PARAM_HORAS_ANTECEDENCIA,
  SUPPORT_CENTER_PARAM_TOLERANCIA_MINUTOS,
} from '../types/supportCenterParameters'

function parseNonNegativeInt(raw: string | undefined | null, fallback: number): number {
  if (raw == null || String(raw).trim() === '') return fallback
  const n = Number.parseInt(String(raw).trim(), 10)
  if (!Number.isFinite(n) || n < 0) return fallback
  return n
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function addLocalDays(d: Date, days: number): Date {
  const next = new Date(d)
  next.setDate(next.getDate() + days)
  return next
}

function toYmd(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

function parseSlotDate(value: string): Date | null {
  if (!value) return null
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'))
  return Number.isNaN(d.getTime()) ? null : d
}

export type SlotBookableReason = 'ok' | 'past' | 'min_hours' | 'max_days'

/**
 * Verifica se um horário pode ser marcado com base nos limites de agendamento.
 * - past: horário já passou
 * - min_hours: falta antecedência mínima em horas
 * - max_days: data além do máximo de dias à frente
 */
export function checkSlotBookable(
  scheduledAt: string | Date,
  limits: SupportCenterSchedulingLimits,
  now: Date = new Date()
): SlotBookableReason {
  const when = typeof scheduledAt === 'string' ? parseSlotDate(scheduledAt) : scheduledAt
  if (!when) return 'past'

  if (when.getTime() <= now.getTime()) return 'past'

  const minMs = limits.minHoursAhead * 60 * 60 * 1000
  if (when.getTime() < now.getTime() + minMs) return 'min_hours'

  const today = startOfLocalDay(now)
  const maxDay = startOfLocalDay(addLocalDays(today, limits.maxDaysAhead))
  const slotDay = startOfLocalDay(when)
  if (slotDay.getTime() > maxDay.getTime()) return 'max_days'

  return 'ok'
}

export function isSlotBookable(
  scheduledAt: string | Date,
  limits: SupportCenterSchedulingLimits,
  now: Date = new Date()
): boolean {
  return checkSlotBookable(scheduledAt, limits, now) === 'ok'
}

export function getSchedulingDateBounds(
  limits: SupportCenterSchedulingLimits,
  now: Date = new Date()
): { minYmd: string; maxYmd: string } {
  const today = startOfLocalDay(now)
  return {
    minYmd: toYmd(today),
    maxYmd: toYmd(addLocalDays(today, limits.maxDaysAhead)),
  }
}

export function slotBookableErrorMessage(
  reason: SlotBookableReason,
  limits: SupportCenterSchedulingLimits
): string | null {
  if (reason === 'ok') return null
  if (reason === 'past') return 'Não é possível agendar em data ou horário que já passou.'
  if (reason === 'min_hours') {
    const h = limits.minHoursAhead
    return `É necessário marcar com pelo menos ${h} hora${h === 1 ? '' : 's'} de antecedência.`
  }
  const d = limits.maxDaysAhead
  return `Só é possível agendar até ${d} dia${d === 1 ? '' : 's'} à frente.`
}

export const supportCenterParametersService = {
  async listParameters(): Promise<SupportCenterParameter[]> {
    const { data, error } = await supabase
      .from('support_center_parameters')
      .select('*')
      .order('key', { ascending: true })

    if (error) throw error
    return (data ?? []) as SupportCenterParameter[]
  },

  async updateParameter(
    id: string,
    data: { value: string; description?: string }
  ): Promise<SupportCenterParameter> {
    const payload: { value: string; description?: string; updated_at: string } = {
      value: data.value,
      updated_at: new Date().toISOString(),
    }
    if (data.description !== undefined) {
      payload.description = data.description
    }

    const { data: result, error } = await supabase
      .from('support_center_parameters')
      .update(payload)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return result as SupportCenterParameter
  },

  async getSchedulingLimits(): Promise<SupportCenterSchedulingLimits> {
    try {
      const params = await this.listParameters()
      const byKey = new Map(params.map((p) => [p.key, p.value]))
      return {
        maxDaysAhead: parseNonNegativeInt(
          byKey.get(SUPPORT_CENTER_PARAM_DIAS_MAX),
          DEFAULT_SCHEDULING_LIMITS.maxDaysAhead
        ),
        minHoursAhead: parseNonNegativeInt(
          byKey.get(SUPPORT_CENTER_PARAM_HORAS_ANTECEDENCIA),
          DEFAULT_SCHEDULING_LIMITS.minHoursAhead
        ),
        pastToleranceMinutes: parseNonNegativeInt(
          byKey.get(SUPPORT_CENTER_PARAM_TOLERANCIA_MINUTOS),
          DEFAULT_SCHEDULING_LIMITS.pastToleranceMinutes
        ),
      }
    } catch {
      return { ...DEFAULT_SCHEDULING_LIMITS }
    }
  },
}
